import * as FileSaver from 'file-saver';
import { action, computed, makeObservable, observable } from 'mobx';

import { endOfMonth, isAfter, isBefore, startOfMonth, sub } from 'date-fns';
import ApiService from '../services/ApiService';

import { formatAsISODate, now } from '../util/DateUtil';
import { DownloadableFile } from './models/DownloadableFile';
import { IInvoiceDetailsRequest } from './models/IInvoiceDetailsRequest';
import { Loadable } from './models/Loadable';
import { Transaction } from './models/Transaction';
import { ISsoStore } from './SsoStore';
import { ITransactionStore } from './TransactionStore';
import { ITranslateStore } from './TranslateStore';
import { IMandatesStore } from './MandatesStore';

type FileStatus = 'ok' | 'loading' | 'error';

export interface IPaymentHistoryStore {
  readonly filterFromDate: Date;
  readonly filterToDate: Date;
  readonly openingDate: Date;
  readonly closingDate: Date;
  readonly transactions: Loadable<Transaction[]>;
  readonly closingBalance: number;
  readonly totalInvoiceAmount: number;
  readonly totalReceiptPaidAmount: number;
  readonly openingBalance: number;
  readonly fileStatus: 'ok' | 'loading' | 'error';

  loadTransactions(): Promise<void>;

  downloadFile(): void;

  onFilterFromDateChange(newDate?: Date): void;

  onFilterToDateChange(newDate?: Date): void;

  toInvoiceDetailsRequest(transaction: Transaction): IInvoiceDetailsRequest;
}

export default class PaymentHistoryStore implements IPaymentHistoryStore {
  @observable
  public filterFromDate: Date = null;
  @observable
  public filterToDate: Date = null;
  @observable
  public openingDate: Date = null;
  @observable
  public closingDate: Date = null;
  @observable
  public fileStatus: FileStatus = 'ok';

  constructor(
    private ssoStore: ISsoStore,
    private transactionStore: ITransactionStore,
    private translateStore: ITranslateStore,
    private apiService: ApiService,
    private mandateStore: IMandatesStore
  ) {
    makeObservable(this);
    this.setFilterFromDate(sub(now(), { months: 3 }));
    this.setFilterToDate(now());
  }

  @computed
  public get transactions(): Loadable<Transaction[]> {
    return this.transactionStore ? this.transactionStore.transactions : null;
  }

  @computed
  public get closingBalance(): number {
    return this.transactionStore.closingBalance;
  }

  @computed
  public get openingBalance(): number {
    return this.transactionStore.openingBalance;
  }

  @computed
  public get totalInvoiceAmount(): number {
    return this.transactionStore ? this.transactionStore.totalInvoiceAmount : 0;
  }

  @computed
  public get totalReceiptPaidAmount(): number {
    return this.transactionStore ? this.transactionStore.totalReceiptAmount : 0;
  }

  @action
  public onFilterFromDateChange = (newFromDate?: Date): void => {
    if (!newFromDate) {
      return;
    }
    this.setFilterFromDate(newFromDate);
    if (isBefore(this.filterToDate, this.filterFromDate)) {
      this.setFilterToDate(this.filterFromDate);
    }
  };

  @action
  public onFilterToDateChange = (newToDate?: Date): void => {
    if (!newToDate) {
      return;
    }
    const isDateInCurrentMonth = isAfter(newToDate, startOfMonth(now()));
    if (isDateInCurrentMonth) {
      this.setFilterToDate(now());
    } else {
      this.setFilterToDate(endOfMonth(newToDate));
    }
    if (isAfter(this.filterFromDate, this.filterToDate)) {
      this.setFilterFromDate(this.filterToDate);
    }
  };

  @action.bound
  public async loadTransactions(): Promise<void> {
    this.closingDate = this.filterToDate;
    this.openingDate = this.filterFromDate;

    await this.mandateStore.mandate.promise;

    if (this.mandateStore.mandate.payload.billingMandateLevel === 'FULL') {
      this.transactionStore.loadTransactions(this.ssoStore.customer, {
        toDate: formatAsISODate(this.closingDate),
        fromDate: formatAsISODate(this.openingDate)
      });
    }
  }

  @action.bound
  public downloadFile(): void {
    const invoiceFilePromise = this.apiService.downloadPaymentHistoryFile(this.ssoStore.customer.customerId, {
      toDate: formatAsISODate(this.closingDate),
      fromDate: formatAsISODate(this.openingDate)
    });
    this.fileStatus = 'loading';
    invoiceFilePromise
      .then((file: DownloadableFile) => {
        this.markDownloadingFinished();
        FileSaver.saveAs(file.rawData, file.fileName);
      })
      .catch(() => this.markDownloadingFailed());
  }

  public toInvoiceDetailsRequest(transaction: Transaction): IInvoiceDetailsRequest {
    return {
      customerId: this.ssoStore.customer.customerId,
      dueDate: transaction.invoiceDueDate,
      invoiceNumber: transaction.invoiceNumber,
      issuedDate: transaction.date,
      referenceNumber: transaction.invoiceReferenceNumber,
      invoiceServiceDetailsAccess: transaction.invoiceServiceDetailsAccess,
      source: transaction.invoiceSource,
      contentType: transaction.invoiceContentType,
      subscriber: null
    };
  }

  @action
  private setFilterFromDate = (fromDate: Date) => {
    this.filterFromDate = startOfMonth(fromDate);
  };

  @action
  private setFilterToDate = (toDate: Date) => {
    this.filterToDate = PaymentHistoryStore.endOfMonthButNotInFuture(toDate);
  };

  @computed
  private get lastTransaction(): Transaction {
    if (!this.transactions.isFinished) {
      return null;
    }
    if (this.transactions.payload.length === 0) {
      return null;
    }
    return this.transactions.payload[0];
  }

  @action
  private markDownloadingFailed(): void {
    this.fileStatus = 'error';
  }

  @action
  private markDownloadingFinished(): void {
    this.fileStatus = 'ok';
  }

  private static endOfMonthButNotInFuture(date: Date) {
    return isAfter(endOfMonth(date), now()) ? now() : endOfMonth(date);
  }
}
